/** The two full-screen materials of the frame (05 §8): the low-resolution post pass and the integer upscale. */

import { type Color, type Data3DTexture, GLSL3, RawShaderMaterial, Vector2 } from 'three';
import { sharedUniforms } from '../materials/toon';
import { LUT_SIZE } from '../palette';
import type { RenderSize } from '../size';
import type { FrameTargets } from './targets';
import fullscreenVert from './shaders/fullscreen.vert.glsl?raw';
import postFrag from './shaders/post.frag.glsl?raw';
import transitionGlsl from './shaders/transition.glsl?raw';
import upscaleFrag from './shaders/upscale.frag.glsl?raw';

const srgbTriple = (c: Color): [number, number, number] => {
  const s = c.clone().convertLinearToSRGB();
  return [s.r, s.g, s.b];
};

/**
 * Outlines, palette lock, fog, grade and the screen transition. Every look uniform starts neutral; the
 * pipeline sets them. `ink` is the outline colour (linear).
 */
export function createPostMaterial(lut: Data3DTexture, ink: Color): RawShaderMaterial {
  const inkSrgb = srgbTriple(ink);
  return new RawShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: fullscreenVert,
    fragmentShader: postFrag.replace('// @transition', transitionGlsl),
    depthTest: false,
    depthWrite: false,
    uniforms: {
      tColor: { value: null },
      tNormalId: { value: null },
      tDepth: { value: null },
      tLut: { value: lut },
      uTexel: { value: new Vector2(1, 1) },
      uCamTexel: sharedUniforms.uCamTexel,
      uNear: { value: 1 },
      uFar: { value: 100 },
      uInk: { value: inkSrgb },
      uFog: { value: [0, 0, 0] },
      uFogAmount: { value: 0 },
      uFogStart: { value: 1 },
      uFogSpan: { value: 4 },
      uCamZ: { value: 0 },
      uHalfH: { value: 1 },
      uSinPitch: { value: 0 },
      uCosPitch: { value: 1 },
      uPaletteLock: { value: 0.85 },
      uDitherAmp: { value: 0.07 },
      uLutSize: { value: LUT_SIZE },
      uTint: { value: [1, 1, 1] },
      uTintAmount: { value: 0 },
      uValueScale: { value: 1 },
      uOutlines: { value: 1 },
      uPlayerRim: { value: inkSrgb },
      uTransPhase: { value: 0 },
      uTransCell: { value: 40 },
      uTransLine: { value: 0.05 },
      uTransFill: { value: [0, 0, 0] },
      uTransRim: { value: [1, 1, 1] },
      uTransWall: { value: [0.5, 0.5, 0.5] },
      uTransShine: { value: [1, 1, 1] },
    },
  });
}

/** Nearest-neighbour blow-up with the sub-texel offset, the vignette and the optional scanlines. */
export function createUpscaleMaterial(): RawShaderMaterial {
  return new RawShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: fullscreenVert,
    fragmentShader: upscaleFrag,
    depthTest: false,
    depthWrite: false,
    uniforms: {
      tImage: { value: null },
      uTargetSize: { value: new Vector2(1, 1) },
      uViewSize: { value: new Vector2(1, 1) },
      uBorder: { value: 1 },
      uSubTexel: { value: new Vector2() },
      uVignette: { value: 0.12 },
      uScale: { value: 1 },
      uScanline: { value: 1 },
      uVignetteBoost: { value: 0 },
    },
  });
}

/** Point both passes at a (new) set of targets of `size`: the textures they read and the sizes they use. */
export function bindTargets(post: RawShaderMaterial, up: RawShaderMaterial, t: FrameTargets, size: RenderSize): void {
  const [color, normalId] = t.scene.textures;
  if (!color || !normalId) throw new Error('scene target has no attachments');
  const set = (m: RawShaderMaterial, name: string, value: unknown): void => {
    const u = m.uniforms[name];
    if (!u) throw new Error(`missing uniform ${name}`);
    u.value = value;
  };
  set(post, 'tColor', color);
  set(post, 'tNormalId', normalId);
  set(post, 'tDepth', t.depth);
  set(post, 'uTexel', new Vector2(1 / size.targetW, 1 / size.targetH));
  set(up, 'tImage', t.post.texture);
  set(up, 'uTargetSize', new Vector2(size.targetW, size.targetH));
  set(up, 'uViewSize', new Vector2(size.viewW, size.viewH));
  set(up, 'uBorder', size.border);
}
